import { useMemo, useState } from "react";
import { Button } from "antd";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { CloseOutlined } from "@ant-design/icons";
import {
  getCodeKey,
  LicenseCodeSelectTable,
} from "../../../global/components/LicenseCodeSelectTable";
import usePost from "../../../global/hooks/usePost";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { itemOptions, resolveItemValue } from "../../../utils/Common";

const getItemValue = (record) => {
  const n = Number(record?.Item);
  return Number.isFinite(n) ? n : null;
};

const getItemTypeLabel = (record) =>
  record?.ItemName ||
  (record?.Item != null ? `Item ${record.Item}` : "this item");

export const LicenseItemPicker = ({
  licenseId,
  existingItems = [],
  onCancel,
  onSaved,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const selectedRowKeys = selectedRows.map((row) => row.key);

  const postUrl = `${process.env.REACT_APP_API_URL}/api/licenses/${licenseId}/items/`;
  const { postData: postAction } = usePost(postUrl);

  const usedItemValues = useMemo(() => {
    const used = new Set();
    existingItems.forEach((row) => {
      const value = resolveItemValue(row, itemOptions);
      if (value != null) used.add(value);
    });
    return used;
  }, [existingItems]);

  const isSelected = (record) => selectedRowKeys.includes(getCodeKey(record));

  // One code per item on a license.
  const getDisabledReason = (record) => {
    const itemValue = getItemValue(record);
    if (usedItemValues.has(itemValue)) {
      return `${getItemTypeLabel(record)} is already added to this license.`;
    }
    const otherSelected = selectedRows.some(
      (row) =>
        row.key !== getCodeKey(record) && getItemValue(row) === itemValue,
    );
    if (otherSelected) {
      return `Only one ${getItemTypeLabel(record)} can be selected. Unselect the other code first.`;
    }
    return null;
  };

  const onToggle = (record) =>
    setSelectedRows((prev) =>
      prev.some((row) => row.key === record.key)
        ? prev.filter((row) => row.key !== record.key)
        : [...prev, record],
    );

  const onSubmit = async () => {
    if (!selectedRows.length) {
      openErrorNotification("Please select at least one item.");
      return;
    }
    setSubmitting(true);
    const failedRows = [];
    for (const row of selectedRows) {
      const result = await postAction({ CodeNo: Number(row.CodeNo) });
      if (result == null) failedRows.push(row);
    }
    setSubmitting(false);

    if (failedRows.length < selectedRows.length) onSaved?.();
    if (!failedRows.length) {
      openConfirmNotification("Success");
      onCancel?.();
      return;
    }
    setSelectedRows(failedRows);
    openErrorNotification(
      failedRows.length === selectedRows.length
        ? "Something went wrong"
        : `${selectedRows.length - failedRows.length} added, ${failedRows.length} failed.`,
    );
  };

  return (
    <>
      <div className="prepurchased-items-form">
        <LicenseCodeSelectTable
          isSelected={isSelected}
          getDisabledReason={getDisabledReason}
          onToggle={onToggle}
        />
      </div>

      <div className="flex justify-center mt-4 gap-4">
        <Button
          type="primary"
          className="mr-2 w-[104px] h-[30px] font-medium"
          loading={submitting}
          disabled={submitting}
          onClick={onSubmit}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          Apply
        </Button>
        <Button
          onClick={onCancel}
          disabled={submitting}
          className="reset-button w-[104px] h-[30px] font-medium"
        >
          <CloseOutlined className="font-medium" />
          Close
        </Button>
      </div>
    </>
  );
};
